import { useEffect, useMemo, useState } from 'react'

import _ from 'lodash'
import { ItList, ItListItem } from 'types/graphql'

import { possessiveForm } from 'src/lib/utils'
import { BrandColor } from 'src/types'

import { getInsideUIData } from './getInsideUIData'
import {
  getItListItemsCountByType,
  Statistic,
  Statistics,
} from './getItListItemsCountByType'

export type InsideItemProps = {
  label: string
  plural: string
  icon: JSX.Element
  description({
    description,
    count,
    creatorName,
    creatorNamePossessive,
  }: {
    description: string
    count: number
    creatorName: string
    creatorNamePossessive: string
  }): string
}

export type InsideItems = {
  [index in Statistic | 'other']: InsideItemProps
}

const INSIDE_TIMEOUT = 500 // max recomputing inside every .5s

export default function useInside({
  brandColor,
  itList,
  itListItems,
  withEmailPaywall,
}: {
  brandColor: BrandColor
  itList: ItList
  itListItems: ItListItem[]
  withEmailPaywall: boolean
}) {
  const creatorImage = itList.creator.profileImage
  const creatorName = itList.creator.firstName
  const [currentInside, setCurrentInside] = useState<Statistics | null>(null)
  const uiData = getInsideUIData(brandColor, creatorName, creatorImage)
  // Debounce computation of inside except for the first time
  const debouncedBuildInside = useMemo(
    () =>
      _.debounce((itListItems: ItListItem[]) => {
        setCurrentInside(getItListItemsCountByType(itListItems))
      }, INSIDE_TIMEOUT),
    []
  )
  useEffect(() => {
    if (currentInside) {
      debouncedBuildInside(itListItems)
    } else {
      setCurrentInside(getItListItemsCountByType(itListItems))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itListItems])
  const creatorNamePossessive = possessiveForm(creatorName)
  const isPaywalled = withEmailPaywall || !!itList.price

  return { uiData, creatorNamePossessive, isPaywalled, currentInside }
}
